import { AlertTriangle } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { t } from '@/i18n';
import { cn } from '@shared/lib';
import { dataErrorKind, isRetryable, type DataErrorKind } from '@/shared/api/data-availability';

type I18nKey = Parameters<typeof t>[0];

/**
 * 위젯 단위 장애 표시 (TRAD-2157).
 *
 * **기준 화면**: `features/financial-calendar/ui/EventList.tsx` 의 `ErrorState`.
 * 그 구조(중앙정렬 · 아이콘 · 문구 · 재시도 버튼)를 그대로 따르되, 세 곳에서 복제하지 않도록
 * 여기로 올렸다. `EventList` 도 이것을 쓴다.
 *
 * ⚠️ 아이콘은 **lucide** 다. 기준 화면 원본은 `⚠️` **이모지**였는데, 감사 SoT-14 가
 * "이모지를 아이콘으로 쓰지 않는다"로 그 파일을 결함으로 지목해 뒀다(W-06 / TRAD-2106).
 * 그대로 복제하면 알려진 결함을 화면 수만큼 퍼뜨리므로, **기준 화면 쪽도 함께 고쳤다.**
 *
 * ⚠️ 문구는 카탈로그 경유(`error.generic.*`). 커뮤니티·공지가 이미 쓰는 키를 재사용한다.
 *
 * 🔴 **장애와 빈 상태는 다르다.** 이 컴포넌트는 **요청이 실패했을 때만** 쓴다.
 * "결과가 0건"에는 절대 쓰지 말 것 — 그건 빈 상태이고 다른 컴포넌트다.
 * (요청 실패에 "없습니다"를 쓰면 화면이 거짓말을 한다 — TRAD-2143 참조)
 *
 * TRAD-2107(2-b) — `shared/ui/widget-error/WidgetErrorState`에서 이 자리(`shared/ui/ErrorState`)로
 * 승격. 22개 소비자의 import·JSX 태그명을 함께 갱신했고, 렌더 로직·className·문구는 승격 전과
 * 바이트 단위로 동일하다(리팩터링 시 디자인 변경 금지 원칙). 구 경로 재export는 남기지 않았다.
 */
interface ErrorStateProps {
  onRetry: () => void;
  /**
   * 실패한 쿼리의 error 객체. **선택**이다 — 넘기지 않으면 렌더는 종전과 완전히 동일하다
   * (기존 22개 소비자는 손대지 않아도 바뀌는 것이 없다).
   *
   * 넘기면 서버 code 로 표시 태도를 가른다 (TRAD-2153):
   * - `E_006`(점검) → 점검 문구 + **재시도 버튼 없음**. 눌러봐야 같은 화면이므로 주지 않는다.
   * - `E_005`(일시적 수집 실패) → 일시 장애 문구 + 재시도 버튼.
   * - 그 외/code 없음 → 기존 일반 문구 + 재시도 버튼.
   */
  error?: unknown;
  /** `sm` = 카드·위젯 안(기본), `md` = 리스트 전체 영역 */
  size?: 'sm' | 'md';
  className?: string;
  /**
   * TRAD-2099 (W-07) — 도메인 전용 제목 문구로 override. **선택**이다 — 넘기지 않으면 기존
   * `kind` 분류 기반 COPY 테이블 그대로 렌더된다(기존 37개 소비자는 손대지 않아도 바뀌는 게
   * 없다). `titleKey`가 우선이고, 없으면 `title`(이미 `t()`로 지역화된 문자열)을 쓴다.
   * 🔴 이 override는 **제목 한 줄만** 바꾼다 — `desc`·재시도 버튼 가시성은 여전히 `error`로
   * 판정한 `kind`를 따른다(점검/영구 실패 판정까지 무력화하지 않기 위함). EmptyState의
   * `title`/`titleKey` 패턴과 동일하게 맞췄다.
   */
  titleKey?: I18nKey;
  /** 이미 `t()`로 지역화된 문자열. 🔴 하드코딩 금지 — 반드시 `t()` 결과만 넘길 것. */
  title?: string;
}

/**
 * 🔴 code 를 모르면 반드시 `generic` 이다 — 알 수 없는 실패를 "점검 중"이라 말하면
 * 화면이 거짓말을 하고, 사용자는 멀쩡한 재시도 기회까지 잃는다.
 */
const COPY: Record<DataErrorKind, { title: I18nKey; desc?: I18nKey }> = {
  generic: { title: 'error.generic.title' },
  unavailable: { title: 'error.data_unavailable.title', desc: 'error.data_unavailable.desc' },
  maintenance: { title: 'error.maintenance.title', desc: 'error.maintenance.desc' },
  // TRAD-2365 — 서버가 200 으로 규격을 어긴 **영구** 상태. 재시도해도 같은 응답이 오므로
  // "일시적인 문제"라고 말하지 않고 재시도 버튼도 주지 않는다(`isRetryable` 이 false).
  'contract-violation': {
    title: 'error.contract_violation.title',
    desc: 'error.contract_violation.desc',
  },
};

export const ErrorState = ({ onRetry, error, size = 'sm', className, titleKey, title }: ErrorStateProps) => {
  const kind = dataErrorKind(error);
  const copy = COPY[kind];
  const displayTitle = title ?? (titleKey ? t(titleKey) : t(copy.title));

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center gap-3',
        size === 'md' ? 'py-20 gap-4' : 'py-8',
        className,
      )}
      role="alert"
    >
      <AlertTriangle className={size === 'md' ? 'h-10 w-10' : 'h-6 w-6'} aria-hidden="true" />
      <p className="text-muted-foreground text-sm">{displayTitle}</p>
      {/* 점검처럼 재시도 버튼이 없는 상태는 "무엇을 기다리면 되는지"를 대신 말해줘야 한다. */}
      {copy.desc && <p className="text-muted-foreground text-xs">{t(copy.desc)}</p>}
      {isRetryable(kind) && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('error.generic.retry')}
        </Button>
      )}
    </div>
  );
};
